import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectsAPI, BillingAPI } from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import axios from "axios";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useAuth } from "@/lib/providers";

export default function NewProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractSummary, setExtractSummary] = useState(null); // { filename, filled: [...], missing: [...] }
  const fileInputRef = useRef(null);
  const existingPackInputRef = useRef(null);
  // Existing on-shelf pack images — mandatory for `packaging_refresh` projects,
  // ignored otherwise. Stored as { b64, mime, name, preview } to match the
  // shape used elsewhere in the assessment flow.
  const [existingPackFiles, setExistingPackFiles] = useState([]);
  const [form, setForm] = useState({
    project_type: "new_packaging",
    brand_name: "",
    client_name: "",
    category: "",
    positioning: "",
    target_audience: "",
    aspirations: "",
    competitors: "",
    competitor_urls: "",
    notes: "",
  });
  // Tracks which fields were auto-filled by the extractor so we can subtly
  // highlight them in the form (blue border) and highlight missing ones in red.
  const [autoFilled, setAutoFilled] = useState(new Set());
  const [pendingFields, setPendingFields] = useState(new Set());
  // Backend 402 paywall fallback — if the backend rejects due to the
  // free-tier 1-project cap, surface the same Buy Credits modal.
  const [showPaywall, setShowPaywall] = useState(false);

  // Mount-time pre-check for direct-URL visitors (deep-link to /projects/new).
  // Free-tier external users who already own a project should hit the
  // paywall BEFORE spending time filling the 3-step wizard. Internal /
  // admin / paying users bypass instantly.
  useEffect(() => {
    const isExternal = user?.user_type === "external" && user?.role !== "admin";
    if (!isExternal) return;
    let cancelled = false;
    (async () => {
      try {
        const [walletRes, projectsRes] = await Promise.all([
          BillingAPI.wallet(),
          ProjectsAPI.list(""),
        ]);
        if (cancelled) return;
        const lifetime = Number(walletRes.data?.wallet?.lifetime_credits_purchased || 0);
        const isFreeTier = lifetime === 0;
        if (isFreeTier && (projectsRes.data || []).length >= 1) {
          setShowPaywall(true);
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("NewProject mount-time cap check failed:", err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_type, user?.role]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Once the user manually edits a field, drop the auto-fill / pending flag.
    if (autoFilled.has(k) || pendingFields.has(k)) {
      const nextA = new Set(autoFilled); nextA.delete(k); setAutoFilled(nextA);
      const nextP = new Set(pendingFields); nextP.delete(k); setPendingFields(nextP);
    }
  };

  const onDeckPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|pptx|ppt)$/i.test(file.name)) {
      toast.error("Upload a .pdf or .pptx presentation.");
      return;
    }
    setExtracting(true);
    setExtractSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/projects/extract_brief`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const filled = [];
      const missing = data._missing_fields || [];
      const nextForm = { ...form };
      const map = {
        brand_name: "brand_name",
        client_name: "client_name",
        category: "category",
        positioning: "positioning",
        target_audience: "target_audience",
        aspirations: "aspirations",
        notes: "notes",
      };
      Object.entries(map).forEach(([k, formKey]) => {
        if (data[k]) { nextForm[formKey] = data[k]; filled.push(formKey); }
      });
      if (Array.isArray(data.competitors) && data.competitors.length) {
        nextForm.competitors = data.competitors.join(", ");
        filled.push("competitors");
      }
      setForm(nextForm);
      setAutoFilled(new Set(filled));
      setPendingFields(new Set(missing));
      setExtractSummary({ filename: file.name, filled, missing });
      toast.success(`Extracted ${filled.length} field${filled.length === 1 ? "" : "s"} from ${file.name}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Extraction failed: ${msg}`);
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Read a File as base64 (without the data: prefix) — used for existing pack images.
  const readFilesAsB64 = (files) => Promise.all(
    files.map((f) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const b64 = String(dataUrl).split(",")[1] || "";
        resolve({
          b64, mime: f.type || "image/png", name: f.name,
          preview: dataUrl, size: f.size,
        });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    }))
  );

  const onExistingPackPicked = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    // Only jpg/png/webp — same allow-list as the assessment dropzones.
    const allowed = picked.filter((f) => /^image\/(png|jpe?g|webp)$/.test(f.type));
    if (allowed.length !== picked.length) {
      toast.error("Only PNG / JPG / WEBP images are accepted for the existing pack.");
    }
    if (!allowed.length) {
      if (existingPackInputRef.current) existingPackInputRef.current.value = "";
      return;
    }
    try {
      const packed = await readFilesAsB64(allowed);
      setExistingPackFiles((prev) => [...prev, ...packed]);
      toast.success(`Added ${packed.length} existing-pack image${packed.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error("Couldn't read one of the images. Please try again.");
    } finally {
      if (existingPackInputRef.current) existingPackInputRef.current.value = "";
    }
  };

  const removeExistingPack = (i) => setExistingPackFiles((prev) => prev.filter((_, idx) => idx !== i));

  const isRefresh = form.project_type === "packaging_refresh";
  const refreshBlockedReason = isRefresh && !existingPackFiles.length
    ? "Upload at least one existing on-shelf pack image inside the Packaging Refresh card before creating the project."
    : "";

  const submit = async () => {
    if (refreshBlockedReason) {
      toast.error(refreshBlockedReason);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        competitors: form.competitors.split(",").map((s) => s.trim()).filter(Boolean),
        competitor_urls: form.competitor_urls.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^https?:\/\//.test(s)),
        existing_pack_images_b64: isRefresh ? existingPackFiles.map((f) => f.b64) : [],
        existing_pack_image_mimes: isRefresh ? existingPackFiles.map((f) => f.mime) : [],
      };
      const { data } = await ProjectsAPI.create(payload);
      navigate(`/projects/${data.project_id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowPaywall(true);
      } else {
        alert("Failed: " + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <div className="overline" data-testid="new-project-overline">New project · Step {step + 1} of 3</div>
      <h1 className="display" style={{ fontSize: "clamp(36px, 5vw, 60px)", marginTop: 12, lineHeight: 1 }}>
        Brief the brand.
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12" style={{ marginTop: 48 }}>
        <div className="md:col-span-3">
          <ol style={{ listStyle: "none", padding: 0, fontFamily: "JetBrains Mono, monospace", fontSize: 12, lineHeight: 2 }}>
            {["Project type", "Brand intake", "Confirm"].map((label, i) => (
              <li key={label} style={{ color: step === i ? "var(--v-text)" : "var(--v-text-2)", borderLeft: step === i ? "2px solid var(--v-accent)" : "2px solid transparent", paddingLeft: 12 }}>
                {String(i + 1).padStart(2, "0")} — {label}
              </li>
            ))}
          </ol>
        </div>
        <div className="md:col-span-9">
          <div className="v-surface" style={{ padding: 32 }}>
            {step === 0 && (
              <div className="v-fade-in">
                <div className="overline">Bucket the project</div>
                <p style={{ marginTop: 8, fontSize: 13, color: "var(--v-text-2)", lineHeight: 1.55 }}>
                  Pick a project type. If you have a client deck (.pdf / .pptx), upload it inside your choice
                  and Claude Sonnet 4.5 will pre-fill the brief for you.
                </p>
                {/* Single hidden file input — shared by both cards. */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.pptx,.ppt,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={onDeckPicked}
                  style={{ display: "none" }}
                  data-testid="brief-extract-input"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ marginTop: 16 }} data-testid="brief-extract-card">
                  {[
                    { id: "new_packaging", t: "Develop a New Packaging or Evaluate an Existing One", b: "Whether you're developing packaging from the ground up or evaluating an existing pack, this assessment helps you measure its effectiveness independently. No previous design or comparison pack is required." },
                    { id: "packaging_refresh", t: "Packaging refresh", b: "Refreshing an existing pack? Evaluate how well the new design retains brand recognition, preserves equity, and builds on what consumers already know." },
                  ].map((opt) => {
                    const selected = form.project_type === opt.id;
                    const isRefreshCard = opt.id === "packaging_refresh";
                    return (
                      <div
                        key={opt.id}
                        className="v-surface"
                        style={{
                          padding: 20,
                          textAlign: "left",
                          outline: selected ? "2px solid var(--v-accent)" : "none",
                          outlineOffset: 2,
                          background: "transparent",
                          display: "flex", flexDirection: "column",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => update("project_type", opt.id)}
                          data-testid={`project-type-${opt.id}`}
                          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
                        >
                          <div className="overline">{opt.id === "new_packaging" ? "01" : "02"}</div>
                          <h3 style={{ marginTop: 8 }}>{opt.t}</h3>
                          <p style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 14 }}>{opt.b}</p>
                        </button>

                        {/* Refresh-only: existing-pack upload lives INSIDE the card
                            so the mandatory input is captured at project-creation time. */}
                        {isRefreshCard && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px dashed var(--v-border)" }}>
                            <div className="overline" style={{ color: selected && existingPackFiles.length ? "var(--v-accent)" : "#f97316", fontSize: 10 }}>
                              Required · existing on-shelf pack
                            </div>
                            <input
                              ref={existingPackInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              multiple
                              onChange={onExistingPackPicked}
                              style={{ display: "none" }}
                              data-testid="existing-pack-input"
                            />
                            <button
                              type="button"
                              data-testid="existing-pack-upload-btn"
                              onClick={() => { if (!selected) update("project_type", opt.id); existingPackInputRef.current?.click(); }}
                              className="v-btn v-btn-outline"
                              style={{ marginTop: 8, width: "100%", justifyContent: "center", fontSize: 12, padding: "10px 12px" }}
                            >
                              {existingPackFiles.length ? `Add more · ${existingPackFiles.length} added` : "Upload existing pack (images)"}
                            </button>
                            {existingPackFiles.length > 0 && (
                              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))", gap: 6 }} data-testid="existing-pack-thumbs">
                                {existingPackFiles.map((f, i) => (
                                  <div key={f.name + i} style={{ position: "relative", aspectRatio: "1/1", border: "1px solid var(--v-border)", overflow: "hidden" }}>
                                    <img src={f.preview} alt={f.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    <button
                                      type="button"
                                      onClick={() => removeExistingPack(i)}
                                      data-testid={`existing-pack-remove-${i}`}
                                      title="Remove"
                                      style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 999, background: "rgba(0,0,0,.7)", color: "#fff", border: "none", fontSize: 10, lineHeight: "14px", cursor: "pointer" }}
                                    >×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mono" style={{ marginTop: 8, color: "var(--v-text-2)", fontSize: 10, lineHeight: 1.5 }}>
                              Every audit on this project scores improvement against these images.
                            </div>
                          </div>
                        )}

                        {/* Deck-upload affordance — anchored inside each project-type card
                            so choosing a type + skipping the typing is one continuous action. */}
                        <div
                          style={{
                            marginTop: "auto",
                            paddingTop: 16,
                            borderTop: "1px dashed var(--v-border)",
                          }}
                        >
                          <div className="overline" style={{ color: selected ? "var(--v-accent)" : "var(--v-text-2)", fontSize: 10 }}>
                            Optional · fast-track
                          </div>
                          <button
                            type="button"
                            data-testid={`brief-extract-btn-${opt.id}`}
                            onClick={() => {
                              // If this card wasn't the selected type, select it first so the
                              // downstream form flow uses the correct project_type.
                              if (!selected) update("project_type", opt.id);
                              fileInputRef.current?.click();
                            }}
                            disabled={extracting}
                            className="v-btn v-btn-outline"
                            style={{ marginTop: 8, width: "100%", justifyContent: "center", fontSize: 12, padding: "10px 12px" }}
                          >
                            {extracting && selected ? "Extracting… (~15–30s)" : "Upload brief (.pdf / .pptx)"}
                          </button>
                          {selected && extractSummary && (
                            <div className="mono" style={{ fontSize: 10, color: "var(--v-text-2)", marginTop: 8, lineHeight: 1.5 }} data-testid={`brief-extract-summary-${opt.id}`}>
                              {extractSummary.filename}
                              <br />
                              <span style={{ color: "var(--v-accent)" }}>{extractSummary.filled.length} filled</span>
                              {extractSummary.missing.length > 0 && (
                                <> · <span style={{ color: "#f97316" }}>{extractSummary.missing.length} missing</span></>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginTop: 32, gap: 6 }}>
                  <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)", letterSpacing: ".08em" }}>
                    {extracting ? "Reading your presentation — one moment…" : "Do not have a presentation?"}
                  </div>
                  <button
                    className="v-btn"
                    data-testid="step-next-1"
                    onClick={() => setStep(1)}
                    disabled={extracting}
                    title={extracting ? "Please wait — the brief is being assimilated." : ""}
                  >
                    {extracting ? "Assimilating brief…" : "Continue to fill information manually →"}
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="v-fade-in">
                {(autoFilled.size > 0 || pendingFields.size > 0) && (
                  <div className="v-surface" style={{ padding: 14, marginBottom: 20, borderLeft: "3px solid var(--v-accent)" }} data-testid="extract-banner">
                    <div className="overline" style={{ color: "var(--v-accent)" }}>Brief extracted</div>
                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      <b>{autoFilled.size}</b> field{autoFilled.size === 1 ? "" : "s"} pre-filled from your deck.
                      {pendingFields.size > 0 && (
                        <>&nbsp;<span style={{ color: "#f97316" }}>{pendingFields.size} field{pendingFields.size === 1 ? "" : "s"} highlighted below</span> — please type manually.</>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Brand name" required value={form.brand_name} onChange={(v) => update("brand_name", v)} testId="field-brand-name" auto={autoFilled.has("brand_name")} pending={pendingFields.has("brand_name")} />
                  <Field label="Client" value={form.client_name} onChange={(v) => update("client_name", v)} testId="field-client-name" auto={autoFilled.has("client_name")} pending={pendingFields.has("client_name")} />
                  <Field label="Category" required value={form.category} onChange={(v) => update("category", v)} placeholder="e.g. Spice masala, snack, beverage" testId="field-category" auto={autoFilled.has("category")} pending={pendingFields.has("category")} />
                  <Field label="Immediate competitors (comma-sep)" value={form.competitors} onChange={(v) => update("competitors", v)} placeholder="MDH, Catch, Everest" testId="field-competitors" auto={autoFilled.has("competitors")} pending={pendingFields.has("competitors")} />
                </div>
                <Field area label="Positioning" required value={form.positioning} onChange={(v) => update("positioning", v)} placeholder="The brand's positioning, voice, role in category…" testId="field-positioning" auto={autoFilled.has("positioning")} pending={pendingFields.has("positioning")} />
                <Field area label="Target audience" required value={form.target_audience} onChange={(v) => update("target_audience", v)} placeholder="Who buys this, where, what life-moment…" testId="field-target-audience" auto={autoFilled.has("target_audience")} pending={pendingFields.has("target_audience")} />
                <Field area label="Aspirations & promise" required value={form.aspirations} onChange={(v) => update("aspirations", v)} placeholder="What does the audience aspire to? What does the brand promise?" testId="field-aspirations" auto={autoFilled.has("aspirations")} pending={pendingFields.has("aspirations")} />
                <Field area label="Competitor product URLs (one per line, optional)" value={form.competitor_urls} onChange={(v) => update("competitor_urls", v)} placeholder={"https://www.amazon.in/...\nhttps://www.flipkart.com/..."} testId="field-competitor-urls" />
                <Field area label="Designer notes (optional)" value={form.notes} onChange={(v) => update("notes", v)} testId="field-notes" auto={autoFilled.has("notes")} pending={pendingFields.has("notes")} />

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                  <button className="v-btn v-btn-outline" data-testid="step-back-1" onClick={() => setStep(0)}>← Back</button>
                  <button
                    className="v-btn"
                    data-testid="step-next-2"
                    disabled={!form.brand_name || !form.category || !form.positioning || !form.target_audience || !form.aspirations}
                    onClick={() => setStep(2)}
                  >
                    Review →
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="v-fade-in">
                <div className="overline">Review</div>
                <h2 style={{ fontSize: 32, marginTop: 8 }}>{form.brand_name}</h2>
                <div className="mono" style={{ color: "var(--v-text-2)" }}>{form.category} · {form.project_type === "packaging_refresh" ? "Refresh" : "New development"}</div>
                <div className="v-divider" style={{ marginTop: 18, paddingTop: 18 }} />
                <Pair k="Positioning" v={form.positioning} />
                <Pair k="Target audience" v={form.target_audience} />
                <Pair k="Aspirations" v={form.aspirations} />
                <Pair k="Competitors" v={form.competitors || "—"} />
                {form.notes && <Pair k="Notes" v={form.notes} />}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32, gap: 20, flexWrap: "wrap" }}>
                  <button className="v-btn v-btn-outline" data-testid="step-back-2" onClick={() => setStep(1)}>← Edit</button>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                    {refreshBlockedReason && (
                      <div className="mono" style={{ fontSize: 11, color: "#f97316", maxWidth: 320, textAlign: "right", lineHeight: 1.5 }} data-testid="refresh-missing-pack-hint">
                        {refreshBlockedReason}
                      </div>
                    )}
                    <button
                      className="v-btn"
                      data-testid="create-project-btn"
                      disabled={submitting || !!refreshBlockedReason}
                      onClick={submit}
                    >
                      {submitting ? "Creating…" : "Create project →"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BuyCreditsModal open={showPaywall} onClose={() => setShowPaywall(false)} creditsAvailable={0} />
    </div>
  );
}

function Field({ label, required, value, onChange, placeholder, area, testId, auto, pending }) {
  // Blue accent when the extractor auto-filled this field; orange when the
  // extractor flagged it as missing and needs manual entry.
  const borderColor = pending ? "#f97316" : auto ? "var(--v-accent)" : undefined;
  const inputStyle = borderColor ? { borderColor, boxShadow: `0 0 0 1px ${borderColor}` } : undefined;
  return (
    <div style={{ marginTop: 16 }}>
      <div className="overline" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <span>{label}{required && <span style={{ color: "var(--v-accent)" }}> *</span>}</span>
        {auto && <span className="mono" style={{ fontSize: 9, color: "var(--v-accent)", letterSpacing: ".12em" }} data-testid={`${testId}-auto`}>AUTO-FILLED</span>}
        {pending && <span className="mono" style={{ fontSize: 9, color: "#f97316", letterSpacing: ".12em" }} data-testid={`${testId}-missing`}>NEEDS INPUT</span>}
      </div>
      {area ? (
        <textarea className="v-textarea" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      ) : (
        <input className="v-input" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      )}
    </div>
  );
}

function Pair({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 24, padding: "10px 0", borderBottom: "1px solid var(--v-border)" }}>
      <div className="overline">{k}</div>
      <div>{v}</div>
    </div>
  );
}
